import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { emApp, ehCelular } from '@/lib/app';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import { useVezDeFolha } from '@/lib/filaFolhas';
import { rotaBloqueiaWhatsapp } from '@/lib/whatsapp';
import {
  buscarMinhaTribo,
  definirMinhaTribo,
  assinarFolhaTribo,
  consumirPedidoFolhaTribo,
  corRgba,
  COR_TRIBO_PADRAO,
  type MinhaTribo,
} from '@/lib/tribo';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Sheet from '@/components/ui/Sheet';
import PrimaryButton from '@/components/ui/PrimaryButton';

// ---------------------------------------------------------------------------
// "De qual tribo você foi como campista?" — folha que sobe UMA vez, na fila de
// abertura (3º lugar, depois de instalar e avisos). Aparece no app para quem
// está logado e ainda não respondeu (minha_tribo().precisa_perguntar). Não tem
// "Agora não": fechar só adia até a próxima abertura.
//
// O perfil usa abrirFolhaTribo() para reabrir e trocar a resposta.
// Não aparece em pagamento/Pix, checkout, ficha, login, caixa, portaria,
// barraca nem modo equipe (mesmas rotas do WhatsApp).
// ---------------------------------------------------------------------------
export default function ConviteTribo() {
  const { ativo: modoEquipe } = useModoEquipe();
  const { pathname } = useLocation();

  const [telaEstreita, setTelaEstreita] = useState(false);
  const [minha, setMinha] = useState<MinhaTribo | null>(null);
  const [forcado, setForcado] = useState(false);
  const [dispensado, setDispensado] = useState(false);
  const [escolha, setEscolha] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  const aquiNoCelular = emApp() || ehCelular() || telaEstreita;
  const rotaBloqueada = rotaBloqueiaWhatsapp(pathname);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const aplicar = () => setTelaEstreita(mq.matches);
    aplicar();
    mq.addEventListener('change', aplicar);
    return () => mq.removeEventListener('change', aplicar);
  }, []);

  const consultar = useCallback(async () => {
    if (modoEquipe || !aquiNoCelular) {
      setMinha(null);
      return;
    }
    try {
      const d = await buscarMinhaTribo();
      setMinha(d);
    } catch {
      /* silencioso: tenta de novo na próxima abertura */
    }
  }, [modoEquipe, aquiNoCelular]);

  useEffect(() => {
    void consultar();
  }, [consultar]);

  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void consultar();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [consultar]);

  // Pedido do perfil: reabre a folha para trocar a resposta.
  useEffect(() => {
    return assinarFolhaTribo(() => {
      if (!consumirPedidoFolhaTribo()) return;
      setForcado(true);
      void (async () => {
        const d = await buscarMinhaTribo().catch(() => null);
        if (d) {
          setMinha(d);
          setEscolha(d.tribo?.id ?? null);
        } else {
          setEscolha(null);
        }
      })();
    });
  }, []);

  const precisaPerguntar = !!minha && minha.logado && minha.precisa_perguntar;
  // O pedido explícito (perfil / página da tribo) abre em qualquer tela;
  // o aparecimento sozinho continua só no celular/app.
  const quer =
    (forcado || (precisaPerguntar && !dispensado)) &&
    !modoEquipe &&
    (aquiNoCelular || forcado) &&
    !rotaBloqueada;
  const minhaVez = useVezDeFolha('convite-tribo', 3, quer && !forcado);

  async function salvar() {
    if (escolha == null || salvando) return;
    setSalvando(true);
    try {
      const t = await definirMinhaTribo(escolha);
      setMinha((m) =>
        m ? { ...m, tribo: t, resposta: 'tribo', precisa_perguntar: false } : m,
      );
      setForcado(false);
      mostrarToast(t ? `Você é de ${t.nome}!` : 'Pronto!', 4000, 'sucesso');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos salvar agora. Tente de novo.'), 6000, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  async function naoFuiCampista() {
    if (salvando) return;
    setSalvando(true);
    try {
      await definirMinhaTribo(null, true);
      setMinha((m) =>
        m ? { ...m, tribo: null, resposta: 'nao_campista', precisa_perguntar: false } : m,
      );
      setForcado(false);
      mostrarToast('Anotado: você não foi campista.', 4000, 'sucesso');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos salvar agora. Tente de novo.'), 6000, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  function fechar() {
    if (forcado) {
      setForcado(false);
    } else {
      setDispensado(true);
    }
  }

  if (!quer) return null;
  if (!minhaVez && !forcado) return null;

  const lista = minha?.opcoes ?? [];

  return (
    <Sheet aberto aoFechar={fechar}>
      <div className="flex flex-col items-center pt-2 text-center">
        <span className="flex h-16 w-16 items-center justify-center text-primary-500">
          <i className="ri-tent-line text-[44px]" aria-hidden />
        </span>
        <h2 className="mt-3 text-[20px] font-extrabold leading-tight text-foreground-950">
          De qual tribo você foi como campista?
        </h2>
        <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-foreground-600">
          É para o placar e as brincadeiras entre as tribos da equipe.
        </p>
      </div>

      {lista.length > 0 && (
        <div className="mt-5 grid grid-cols-3 gap-2">
          {lista.map((t) => {
            const ativa = escolha === t.id;
            const cor = t.cor ?? COR_TRIBO_PADRAO;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setEscolha(t.id)}
                aria-pressed={ativa}
                className={`aperta flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-2 text-[13px] font-semibold text-foreground-950 transition-colors ${
                  ativa ? 'border-2' : 'border border-background-200'
                }`}
                style={{
                  backgroundColor: corRgba(cor, ativa ? 0.22 : 0.1),
                  borderColor: ativa ? cor : undefined,
                }}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: cor }}
                  aria-hidden
                />
                <span className="truncate">{t.nome}</span>
              </button>
            );
          })}
        </div>
      )}

      <PrimaryButton
        cheio
        className="mt-3"
        carregando={salvando}
        disabled={escolha == null}
        onClick={salvar}
      >
        Salvar
      </PrimaryButton>

      <button
        type="button"
        onClick={naoFuiCampista}
        disabled={salvando}
        className="aperta mx-auto mt-2 block cursor-pointer whitespace-nowrap py-2.5 text-[14px] font-semibold text-foreground-500 disabled:opacity-60"
      >
        Não fui campista
      </button>
    </Sheet>
  );
}